"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"
import { Avatar } from "@/components/common/avatar"
import { localeMetadata, routing, type AppLocale } from "@/i18n/routing"
import SwipeGesture from "@/utils/swipe-gesture"

interface AvatarCarouselProps {
  name: string
  images: string[]
  className?: string
  autoScrollIntervalMs?: number
}

export function AvatarCarousel({
  name,
  images,
  className = "",
  autoScrollIntervalMs = 5000,
}: AvatarCarouselProps) {
  const locale = useLocale()
  const tCarousel = useTranslations("common.carousel")
  const validImages = useMemo(() => images.filter(Boolean), [images])
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMulti = validImages.length > 1
  const timerRef = useRef<number | null>(null)
  const swipeGesture = useRef(new SwipeGesture(50))
  const currentLocale =
    localeMetadata[locale as AppLocale] ??
    localeMetadata[routing.defaultLocale as AppLocale]

  const goTo = (index: number) => {
    if (!isMulti) return
    const nextIndex = (index + validImages.length) % validImages.length
    setCurrentIndex(nextIndex)
  }

  const next = () => goTo(currentIndex + 1)
  const prev = () => goTo(currentIndex - 1)

  useEffect(() => {
    if (!isMulti) return
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setCurrentIndex((i) => (i + 1) % validImages.length)
    }, autoScrollIntervalMs) as unknown as number
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
    }
  }, [autoScrollIntervalMs, isMulti, validImages.length])

  // If there is no image, render a single Avatar to keep behavior consistent
  if (validImages.length === 0) {
    return (
      <Avatar
        name={name}
        imageUrl={undefined}
        className={className}
      />
    )
  }

  // Single image: fall back to normal Avatar
  if (!isMulti) {
    return (
      <Avatar
        name={name}
        imageUrl={validImages[0]}
        className={className}
      />
    )
  }

  return (
    <div className="group flex flex-col items-center gap-3">
      {/* Main carousel container with navigation arrows */}
      <div className="relative flex items-center gap-3">
        {/* Left Arrow */}
        <button
          type="button"
          aria-label={tCarousel("previous")}
          onClick={prev}
          className="rounded-full bg-white/80 text-teal-800 shadow hover:bg-white p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        >
          {currentLocale.isRtl ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>

        {/* Avatar container */}
        <div
          className="relative touch-pan-y"
          onTouchStart={(e) => {
            const touch = e.touches[0]
            if (!touch) return

            swipeGesture.current.start(touch.clientX, touch.clientY)
          }}
          onTouchMove={(e) => {
            const touch = e.touches[0]
            if (!touch) return

            swipeGesture.current.move(touch.clientX, touch.clientY)
          }}
          onTouchEnd={() => {
            const direction = swipeGesture.current.end()
            if (direction === "left") {
              next()
            }

            if (direction === "right") {
              prev()
            }
          }}
          onTouchCancel={() => {
            swipeGesture.current.end()
          }}
        >
          {validImages.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className={`absolute inset-0 transition-opacity duration-500 ease-out ${
                idx === currentIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              <Avatar
                name={name}
                imageUrl={url}
                className={className}
              />
            </div>
          ))}
          {/* Ensure layout height on first render */}
          <div className="invisible">
            <Avatar
              name={name}
              imageUrl={validImages[0]}
              className={className}
            />
          </div>
        </div>

        {/* Right Arrow */}
        <button
          type="button"
          aria-label={tCarousel("next")}
          onClick={next}
          className="rounded-full bg-white/80 text-teal-800 shadow hover:bg-white p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        >
          {currentLocale.isRtl ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
      </div>

      {/* Pagination dots below the avatar */}
      <div className="flex justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {validImages.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              i === currentIndex ? "bg-teal-600" : "bg-teal-200"
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export default AvatarCarousel
